import type { AttestationDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { AttestationRecordResponseRow } from '@risksmart-app/drizzle/src/types';
import type {
  Attestation_Record_Bool_Exp,
  GetPolicyAttestationRecordsQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetPolicyAttestationRecordsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, type StrictMappedGraphQLSelection } from 'src/utils';

import { ATTESTATION_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
} from '@/hooks/queries/dashboardFilterArgs';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

const useAttestationsGraphqlVariables = (
  args: UseGetAttestationsRegisterArgs
) => {
  const { userId } = args;
  const whereFilter = useEntityWhereFilter<Attestation_Record_Bool_Exp>(
    Parent_Type_Enum.AttestationRecord
  );

  const userWhere: Attestation_Record_Bool_Exp = {
    UserId: { _eq: userId },
  };

  const { where } = buildDashboardWhere({
    base: userId ? userWhere : whereFilter,
    filters: args,
    datePredicates: ATTESTATION_DATE_PREDICATES,
  });

  return { where };
};

/**
 * Maps TRPC attestation record data to match the GraphQL query structure
 */
export function mapTrpcAttestationRecordsToGraphQL(
  trpcData: AttestationRecordResponseRow[]
): StrictMappedGraphQLSelection<GetPolicyAttestationRecordsQuery> {
  return {
    attestation_record: trpcData,
  };
}

/**
 * Attestation declares no tag or department filter and its ownership story is
 * the pre-existing per-user `userId` arg, not the shared eight-flag fragment
 * (COI-697) — so the dashboard half is dates only. `userId` is NOT part of
 * `DashboardFilterArgs`: it is a genuine caller-supplied filter that predates
 * COI-691 and must keep its own `userId ? userWhere : whereFilter` precedence.
 */
export type UseGetAttestationsRegisterArgs = {
  userId?: string;
} & Pick<DashboardFilterArgs<AttestationDashboardDateField>, 'dateRange'>;

export const useGetAttestationsRegister = createQueryHook<
  UseGetAttestationsRegisterArgs,
  AttestationRecordResponseRow[],
  GetPolicyAttestationRecordsQuery
>({
  trpcQueryOptions: (trpc, { userId, dateRange }) =>
    trpc.frontend.attestation.register.queryOptions({ userId, dateRange }),
  mapTrpcDataToGraphQL: mapTrpcAttestationRecordsToGraphQL,
  graphqlDocument: GetPolicyAttestationRecordsDocument,
  graphqlVariables: useAttestationsGraphqlVariables,
});
